/**
 * 状态类动效：等待中、成功、需要注意。
 *
 * 关于无限循环：GSAP 官方文档在 HyperFrames（逐帧渲染视频）场景下禁止 repeat: -1，
 * 因为视频必须是有限长度。这里是网页，只要卸载时杀干净就可以放心循环，
 * useGSAP 的 revertOnUpdate 会负责清理。
 */

import { useGSAP } from '@gsap/react';
import { useRef, type RefObject } from 'react';

import { MOTION, gsap, prefersReducedMotion } from './gsap';

/**
 * 生成菜谱时的呼吸动效。
 * 这不是装饰：在等待模型返回的几秒里，它回答"还在干活吗"这个疑问。
 */
export function useGeneratingPulse<T extends HTMLElement = HTMLDivElement>(
	active: boolean,
	selector = '[data-pulse]',
): RefObject<T> {
	const scope = useRef<T | null>(null);

	useGSAP(
		() => {
			if (!active || !scope.current || prefersReducedMotion()) return;

			const targets = scope.current.querySelectorAll(selector);
			if (targets.length === 0) return;

			const timeline = gsap.timeline({ repeat: -1, yoyo: true });
			timeline.to(targets, {
				autoAlpha: 0.34,
				duration: 0.68,
				stagger: 0.14,
				ease: 'sine.inOut',
			});

			return () => {
				timeline.kill();
			};
		},
		{ scope, dependencies: [active], revertOnUpdate: true },
	);

	return scope as RefObject<T>;
}

/** 一次成功的弹性反馈。用于记录入库、识别出结果这些时刻。 */
export function playPop(element: HTMLElement | null): void {
	if (!element || prefersReducedMotion()) return;
	gsap.fromTo(
		element,
		{ scale: 0.93 },
		{ scale: 1, duration: 0.42, ease: MOTION.ease.back, clearProps: 'transform' },
	);
}

/** 闪一下，把注意力引到需要人工核对的地方 */
export function playAttention(element: HTMLElement | null): void {
	if (!element || prefersReducedMotion()) return;
	const timeline = gsap.timeline();
	timeline
		.fromTo(
			element,
			{ boxShadow: 'inset 0 0 0 0 var(--warn-soft)' },
			{
				boxShadow: 'inset 0 0 0 2px var(--warn)',
				duration: 0.26,
				ease: 'power2.out',
			},
		)
		.to(element, {
			boxShadow: 'inset 0 0 0 2px transparent',
			duration: 0.5,
			ease: 'power2.in',
			clearProps: 'box-shadow',
		});
}

/** 新项目进入列表时的一次强调，用在"刚加进去的食材"上 */
export function playHighlight(element: HTMLElement | null): void {
	if (!element || prefersReducedMotion()) return;
	gsap.fromTo(
		element,
		{ backgroundColor: 'var(--accent-soft)' },
		{
			backgroundColor: 'rgba(0,0,0,0)',
			duration: 0.9,
			ease: 'power2.out',
			clearProps: 'background-color',
		},
	);
}
